import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../../Context/TenantContext";
import API_BASE_URL from "../../Config/config";
import { checkClickStatus, recordButtonClick, hasUnlimitedClicks } from "../../utils/mockInterviewClickTracker";

const categorizedSections = {
 Quantitative: [
 {
 title:"Number Systems",
 description: `Covers fundamentals like integers, decimals, and fractions.
Includes LCM and HCF, divisibility, and prime numbers.
Important for understanding the base of arithmetic operations.
Mastering this topic builds strong problem-solving roots.
Also includes even-odd, place value, and number classification.`,
 link:"/questions/number-systems",
 image:"https://placehold.co/150?text=Number%20Systems",
 },
 {
 title:"Percentage",
 description: `Deals with calculating percentages, increases, and decreases.
Includes percentage change, successive percentage, and comparisons.
Helps in solving profit-loss, interest, and ratio-based questions.
Commonly tested in various real-life and exam scenarios.
Understanding this boosts speed and accuracy in arithmetic.`,
 link:"/questions/percentage",
 image:"https://placehold.co/150?text=Percentage",
 },
 {
 title:"Profit and Loss",
 description: `Focuses on concepts like cost price, selling price, and profit/loss.
Includes markup, discount, and successive profit/loss situations.
A key application of percentages and basic arithmetic.
Important for commerce, business math, and competitive exams.
Real-life relevance makes it a high-frequency test topic.`,
 link:"/questions/profit-loss",
 image:"https://placehold.co/150?text=Profit%20and%20Loss",
 },
 {
 title:"Simple Interest",
 description: `Covers interest calculated on the principal amount.
Includes formula-based questions for different time durations.
Straightforward and commonly tested in banking exams.
Useful in understanding loan and borrowing fundamentals.
Lays foundation for learning compound interest later.`,
 link:"/questions/simple-interest",
 image:"https://placehold.co/150?text=Simple%20Interest",
 },
 {
 title:"Compound Interest",
 description: `Builds on the concept of simple interest with added complexity.
Focuses on interest on interest over multiple periods.
Includes compounding yearly, half-yearly, and quarterly.
Important for finance, banking, and investment topics.
Requires formula knowledge and clarity in percentage change.`,
 link:"/questions/compound-interest",
 image:"https://placehold.co/150?text=Compound%20Interest",
 },
 {
 title:"Time and Work",
 description: `Examines the rate at which work is completed individually or in groups.
Focuses on man-hours, work efficiency, and combined work.
Includes alternate days work, pipes analogy, and work sharing.
Important for aptitude tests in job recruitment and exams.
Requires careful attention to unit consistency and ratios.`,
 link:"/questions/time-work",
 image:"https://placehold.co/150?text=Time%20and%20Work",
 },
 {
 title:"Time, Speed & Distance",
 description: `Focuses on the relationship between speed, distance, and time.
Includes relative speed, average speed, and meeting points.
Essential for solving problems on trains, cars, and races.
Common in exams and real-life travel/logistics calculations.
Requires unit consistency and strong algebraic manipulation.`,
 link:"/questions/time-speed-distance",
 image:
"https://placehold.co/150?text=Time%2C%20Speed%20%26%20Distance",
 },
 {
 title:"Ratio and Proportion",
 description: `Covers comparison of quantities and their relationships.
Includes direct, inverse, and compound proportions.
Essential for solving mixture, partnership, and age problems.
Forms the base of many arithmetic and data interpretation topics.
Requires understanding simplification and cross-multiplication.`,
 link:"/questions/ratio-proportion",
 image:"https://placehold.co/150?text=Ratio%20and%20Proportion",
 },
 {
 title:"Averages",
 description: `Explores basic average and weighted average calculations.
Includes problems on number series, ages, and groups.
Often integrated into time, speed, and mixture questions.
Conceptually simple but used in tricky scenarios.
Important for data interpretation and quick estimation.`,
 link:"/questions/averages",
 image:"https://placehold.co/150?text=Averages",
 },
 {
 title:"Algebra",
 description: `Involves solving equations, inequalities, and expressions.
Covers identities, linear and quadratic equations.
Foundational topic for higher mathematics and problem solving.
Tests both analytical and symbolic manipulation skills.
Useful across quantitative aptitude, engineering, and more.`,
 link:"/questions/algebra",
 image:"https://placehold.co/150?text=Algebra",
 },
 {
 title:"Geometry",
 description: `Focuses on shapes, angles, lines, and geometric theorems.
Includes triangles, circles, polygons, and their properties.
Tests concepts like congruence, similarity, and Pythagoras.
Visual reasoning and diagrammatic analysis are key.
Essential for spatial intelligence and engineering exams.`,
 link:"/questions/geometry",
 image:"https://placehold.co/150?text=Geometry",
 },
 {
 title:"Mensuration",
 description: `Calculates area, volume, and surface area of 2D/3D figures.
Includes cubes, cylinders, cones, spheres, and more.
Requires formula memorization and application speed.
Useful in construction, packaging, and geometry-based questions.
Visualizing shapes helps in reducing errors.`,
 link:"/questions/mensuration",
 image:"https://placehold.co/150?text=Mensuration",
 },
 {
 title:"Permutation & Combination",
 description: `Covers arrangements (permutations) and selections (combinations).
Includes cases with repetition and restrictions.
Important in counting problems, probability, and logic puzzles.
Requires careful analysis of question conditions.
Builds foundation for advanced probability concepts.`,
 link:"/questions/permutation-combination",
 image:
"https://placehold.co/150?text=Permutation%20%26%20Combination",
 },
 {
 title:"Probability",
 description: `Deals with likelihood of events occurring.
Includes cards, dice, coins, and event-based problems.
Connects to permutations and combinations.
Key for logical thinking and risk estimation.
Requires clear understanding of independent/dependent events.`,
 link:"/questions/probability",
 image:"https://placehold.co/150?text=Probability",
 },
 {
 title:"Mixtures and Alligations",
 description: `Solves problems involving mixing two or more entities.
Applies rule of alligation to compute ratios quickly.
Used in questions on price, concentration, and time.
Requires blend of averages and ratio knowledge.
Widely tested in exams and business-related scenarios.`,
 link:"/questions/mixtures",
 image:
"https://placehold.co/150?text=Mixtures%20and%20Alligations",
 },
 {
 title:"Boats and Streams",
 description: `Focuses on upstream and downstream motion.
Calculates effective speed in water with current resistance.
Connects concepts of time, speed, and direction.
Requires strong equation formulation.
Commonly appears in aptitude and logic exams.`,
 link:"/questions/boats-streams",
 image:"https://placehold.co/150?text=Boats%20and%20Streams",
 },
 {
 title:"Pipes and Cisterns",
 description: `Analyzes problems involving filling and emptying tanks.
Similar to time and work but with inflow/outflow logic.
Includes alternate pipe usage and leak problems.
Tests efficiency-based problem solving.
Diagrams often help in visualizing water levels.`,
 link:"/questions/pipes-cisterns",
 image:"https://placehold.co/150?text=Pipes%20and%20Cisterns",
 },
 {
 title:"Surds and Indices",
 description: `Involves simplifying irrational numbers and exponents.
Covers laws of indices, roots, and rationalization.
Foundation for algebra and logarithms.
Requires clarity in manipulating expressions.
Tests simplification and mental calculation speed.`,
 link:"/questions/surds-indices",
 image:"https://placehold.co/150?text=Surds%20and%20Indices",
 },
 {
 title:"Logarithms",
 description: `Covers properties and computations using logarithms.
Solves exponential equations and simplifies expressions.
Includes base conversion and log identities.
Helpful in handling large numbers and complex formulas.
Requires understanding of both arithmetic and algebra.`,
 link:"/questions/logarithms",
 image:"https://placehold.co/150?text=Logarithms",
 },
 ],
 Qualitative: [
 {
 title:"Sentence Correction",
 description: `Tests grammar, sentence structure, and clarity.
Includes errors in tense, modifiers, subject-verb agreement.
Requires strong knowledge of formal English usage.
Often includes subtle stylistic or logical flaws.
Focus on meaning preservation while correcting.`,
 link:"/questions/sentence-correction",
 image:"https://placehold.co/150?text=Sentence%20Correction",
 },
 {
 title:"Para Jumbles",
 description: `Involves rearranging sentences into logical order.
Tests understanding of sequence, flow, and coherence.
Key indicators include pronouns, connectors, and chronology.
Often used to assess comprehension without a direct question.
Helps develop structural and analytical reading skills.`,
 link:"/questions/para-jumbles",
 image:"https://placehold.co/150?text=Para%20Jumbles",
 },
 {
 title:"Cloze Test",
 description: `Fill-in-the-blank exercises with contextual vocabulary.
Tests grammar, comprehension, and logical consistency.
Requires predicting missing words using sentence flow.
Understanding tone and tense is essential.
Often contains 5–10 blanks in a passage format.`,
 link:"/questions/cloze-test",
 image:"https://placehold.co/150?text=Cloze%20Test",
 },
 {
 title:"Synonyms",
 description: `Tests understanding of words with similar meanings.
Requires a good grasp of vocabulary and context.
Important for reading comprehension and word replacement.
Often includes nuanced or slightly different word pairs.
Eliminating incorrect choices improves accuracy.`,
 link:"/questions/synonyms",
 image:"https://placehold.co/150?text=Synonyms",
 },
 {
 title:"Antonyms",
 description: `Focuses on words with opposite meanings.
Tests knowledge of vocabulary in a broad range.
Useful in strengthening understanding of word usage.
Context plays a crucial role in choosing the right antonym.
Often appears in quick vocabulary or verbal sections.`,
 link:"/questions/antonyms",
 image:"https://placehold.co/150?text=Antonyms",
 },
 {
 title:"Vocabulary Usage",
 description: `Tests appropriate use of words in different contexts.
Goes beyond memorization to understand practical meaning.
Involves usage in sentences, idioms, and expressions.
Crucial for scoring in verbal ability and reading sections.
Building vocabulary through reading improves performance.`,
 link:"/questions/vocabulary-usage",
 image:"https://placehold.co/150?text=Vocabulary%20Usage",
 },
 {
 title:"One Word Substitution",
 description: `Involves replacing phrases with a single word.
Tests vocabulary conciseness and precision.
Helps enhance language fluency and comprehension.
Commonly drawn from idiomatic and formal usage.
Regular practice improves recall and application.`,
 link:"/questions/one-word-substitution",
 image:"https://placehold.co/150?text=One%20Word%20Substitution",
 },
 {
 title:"Idioms and Phrases",
 description: `Focuses on commonly used English expressions.
Tests both meaning and contextual usage.
Often culturally rooted and non-literal.
Understanding improves fluency and comprehension.
Appears frequently in error spotting and usage tests.`,
 link:"/questions/idioms-phrases",
 image:"https://placehold.co/150?text=Idioms%20and%20Phrases",
 },
 {
 title:"Spelling Errors",
 description: `Tests ability to spot incorrect spellings.
Requires visual familiarity with word patterns.
British vs American spelling differences may be tested.
Common in basic language assessments.
Improves accuracy in writing and editing tasks.`,
 link:"/questions/spelling-errors",
 image:"https://placehold.co/150?text=Spelling%20Errors",
 },
 {
 title:"Active-Passive Voice",
 description: `Tests transformation between active and passive sentences.
Requires grammatical understanding of tense and structure.
Preserving meaning while changing voice is key.
Useful in formal and objective writing skills.
Common in school grammar and competitive exams.`,
 link:"/questions/active-passive-voice",
 image:"https://placehold.co/150?text=Active-Passive%20Voice",
 },
 {
 title:"Direct-Indirect Speech",
 description: `Focuses on converting dialogue into reported speech.
Tests tense rules, pronoun usage, and connectors.
Includes interrogative, imperative, and exclamatory forms.
Requires understanding sentence transformation logic.
Common in grammar correction and language papers.`,
 link:"/questions/direct-indirect-speech",
 image:"https://placehold.co/150?text=Direct-Indirect%20Speech",
 },
 {
 title:"Subject-Verb Agreement",
 description: `Ensures subject and verb match in number and person.
Commonly tested in error spotting and sentence correction.
Requires attention to compound and collective subjects.
Verb forms vary in complex sentences and clauses.
A fundamental rule in grammar-based sections.`,
 link:"/questions/subject-verb-agreement",
 image:"https://placehold.co/150?text=Subject-Verb%20Agreement",
 },
 {
 title:"Sentence Completion",
 description: `Tests logical completion of partially written sentences.
Requires understanding tone, tense, and sentence flow.
Options may test vocabulary and contextual relevance.
Improves reading comprehension and decision-making.
Often part of verbal reasoning tests.`,
 link:"/questions/sentence-completion",
 image:"https://placehold.co/150?text=Sentence%20Completion",
 },
 {
 title:"Error Spotting",
 description: `Involves identifying grammatical mistakes in sentences.
Tests multiple rules: tenses, articles, prepositions, etc.
May include idioms, redundancies, or word order issues.
Regular grammar practice is essential for accuracy.
Quick scanning and elimination techniques help.`,
 link:"/questions/error-spotting",
 image:"https://placehold.co/150?text=Error%20Spotting",
 },
 {
 title:"Comprehension Inference",
 description: `Requires drawing conclusions not directly stated.
Tests reading between the lines and logical deduction.
Important for assessing depth of understanding.
Often appears in higher-level comprehension tasks.
Answers are subtle and must be text-supported.`,
 link:"/questions/comprehension-inference",
 image:"https://placehold.co/150?text=Comprehension%20Inference",
 },
 {
 title:"Analogies (Verbal)",
 description: `Tests relationship between pairs of words.
Helps assess pattern recognition and vocabulary depth.
Often framed as"A is to B as C is to ?".
Common in reasoning and verbal assessments.
Useful for improving logical verbal connections.`,
 link:"/questions/verbal-analogies",
 image:"https://placehold.co/150?text=Analogies%20(Verbal)",
 },
 {
 title:"Word Usage",
 description: `Tests correct use of a word in varied contexts.
May involve homonyms, confusing pairs, or misused terms.
Focus is on clarity, grammar, and appropriateness.
Important for avoiding errors in professional writing.
Improves overall communication and comprehension.`,
 link:"/questions/word-usage",
 image:"https://placehold.co/150?text=Word%20Usage",
 },
 {
 title:"Ordering of Words",
 description: `Requires arranging jumbled words into a proper sentence.
Tests grammar, syntax, and sentence structure.
Useful for improving fluency and language processing.
Common in school-level grammar and exams.
Elimination and logical clues help solve quickly.`,
 link:"/questions/ordering-of-words",
 image:"https://placehold.co/150?text=Ordering%20of%20Words",
 },
 {
 title:"Phrase Replacement",
 description: `Tests ability to replace underlined parts meaningfully.
Involves grammar, context, and conciseness.
Requires strong language intuition and rule knowledge.
Common in competitive verbal sections and corrections.
Answer must retain meaning while improving quality.`,
 link:"/questions/phrase-replacement",
 image:"https://placehold.co/150?text=Phrase%20Replacement",
 },
 {
 title:"Reading Comprehension",
 description: `Enhance your reading comprehension by identifying and replacing underlined phrases
 with more effective alternatives. This exercise tests your understanding of grammar, tone, and 
 context while challenging your ability to improve clarity and conciseness.`,
 link:"/questions/phrase-replacement",
 image:"https://placehold.co/150?text=Phrase%20Replacement",
 },
 ],
 Reasoning: [
 {
 title:"Blood Relations",
 description: `Tests understanding of family hierarchies and relations.
Questions involve generations, gender, and logical links.
Requires drawing or visualizing family trees.
Often combined with puzzles and directions.
Accuracy and speed improve with practice.`,
 link:"/questions/blood-relations",
 image:"https://placehold.co/150?text=Blood%20Relations",
 },
 {
 title:"Seating Arrangement",
 description: `Involves arranging people in a line or circle.
Tests spatial reasoning and logical deductions.
May include constraints like facing directions or distances.
Often comes as complex multi-statement puzzles.
Key topic in logical reasoning and aptitude exams.`,
 link:"/questions/seating-arrangement",
 image:"https://placehold.co/150?text=Seating%20Arrangement",
 },
 {
 title:"Syllogisms",
 description: `Tests logical deduction using statements and conclusions.
Includes categorical logic: all, some, none type problems.
Requires understanding of Venn diagrams and logical flow.
May involve either conclusion I, II, or both.
Essential for exams with analytical reasoning sections.`,
 link:"/questions/syllogisms",
 image:"https://placehold.co/150?text=Syllogisms",
 },
 {
 title:"Coding-Decoding",
 description: `Involves letter and number pattern-based codes.
Tests pattern recognition and reverse logic application.
May include symbolic coding or substitution logic.
Often seen in logical reasoning and banking exams.
Speed and accuracy are essential for high scores.`,
 link:"/questions/coding-decoding",
 image:"https://placehold.co/150?text=Coding-Decoding",
 },
 {
 title:"Puzzles",
 description: `Consists of multi-statement logic-based scenarios.
Tests sequencing, grouping, assignment, or comparison.
High weightage topic in reasoning sections.
Requires systematic tabular or diagram-based solving.
Best tackled with practice and pattern identification.`,
 link:"/questions/puzzles",
 image:"https://placehold.co/150?text=Puzzles",
 },
 {
 title:"Series (Number)",
 description: `Focuses on identifying patterns in numeric sequences.
Includes arithmetic, geometric, and complex logic series.
Often used to test calculation speed and observation.
Requires recognizing the logic of progression.
Frequent topic in aptitude and IQ tests.`,
 link:"/questions/number-series",
 image:"https://placehold.co/150?text=Series%20(Number)",
 },
 {
 title:"Series (Alphabet)",
 description: `Involves finding patterns in letter sequences.
Tests knowledge of alphabetical order and logic.
May include reverse, skipping, or alternating sequences.
Often combined with position-based logic.
Improves attention to detail and verbal logic.`,
 link:"/questions/alphabet-series",
 image:"https://placehold.co/150?text=Series%20(Alphabet)",
 },
 {
 title:"Cause & Effect",
 description: `Tests logical connection between two statements.
Questions ask if one causes the other or both are independent.
Requires reasoning beyond the literal statement.
Eliminating unrelated or vaguely linked options is key.
Helpful in decision-making and critical thinking practice.`,
 link:"/questions/cause-effect",
 image:"https://placehold.co/150?text=Cause%20&%20Effect",
 },
 {
 title:"Statement & Assumptions",
 description: `Tests what assumptions are implied in a given statement.
Focuses on identifying unstated yet valid ideas.
Requires understanding of implied logic and reasoning.
Carefully worded traps test attention and analysis.
Common in managerial and critical thinking tests.`,
 link:"/questions/statement-assumptions",
 image:"https://placehold.co/150?text=Statement%20&%20Assumptions",
 },
 {
 title:"Statement & Conclusions",
 description: `Focuses on evaluating conclusions from a given statement.
Tests deductive reasoning based on given facts.
Important to avoid external knowledge and assumptions.
Questions often have two conclusions to assess.
Popular in banking, SSC, and other aptitude tests.`,
 link:"/questions/statement-conclusions",
 image:"https://placehold.co/150?text=Statement%20&%20Conclusions",
 },
 {
 title:"Input-Output",
 description: `Involves transformation of sequences across steps.
Tests identification of machine-like logic in rearrangement.
Patterns may be based on numbers, letters, or both.
Analyzing all steps is key to solving future ones.
Common in reasoning-intensive competitive exams.`,
 link:"/questions/input-output",
 image:"https://placehold.co/150?text=Input-Output",
 },
 {
 title:"Data Sufficiency",
 description: `Tests if given data is enough to answer a question.
Focuses on logical completeness, not actual solution.
Often comes with two statements to compare sufficiency.
Applicable in quantitative and reasoning sections.
Critical for time-saving and smart solving techniques.`,
 link:"/questions/data-sufficiency",
 image:"https://placehold.co/150?text=Data%20Sufficiency",
 },
 {
 title:"Decision Making",
 description: `Presents situations with conditions and multiple choices.
Tests judgment, ethics, and problem-solving logic.
Often found in managerial aptitude and HR papers.
Options may all seem valid - choose the best fit.
Elimination method works well in tricky scenarios.`,
 link:"/questions/decision-making",
 image:"https://placehold.co/150?text=Decision%20Making",
 },
 {
 title:"Direction Sense",
 description: `Tests ability to track movement and directions.
Questions involve left, right, distance, and facing.
Requires visual tracking or sketching the movement.
Often paired with turns, angles, or end position queries.
Quick drawing aids in error-free solving.`,
 link:"/questions/direction-sense",
 image:"https://placehold.co/150?text=Direction%20Sense",
 },
 {
 title:"Ranking and Order",
 description: `Involves determining position from top/bottom/side.
Tests logical sequencing of ranks and orders.
Useful in linear arrangements and competitive lists.
Requires quick processing of relative positions.
Practice improves speed and reduces confusion.`,
 link:"/questions/ranking-order",
 image:"https://placehold.co/150?text=Ranking%20and%20Order",
 },
 {
 title:"Cubes and Dice",
 description: `Tests visualization of 2D and 3D figures.
Includes unfolded dice patterns and cube cuts/colors.
Requires spatial ability and mental rotation skills.
Also tests face-opposite or same-side logic.
Frequently asked in SSC, railways, and aptitude tests.`,
 link:"/questions/cubes-dice",
 image:"https://placehold.co/150?text=Cubes%20and%20Dice",
 },
 {
 title:"Calendar Problems",
 description: `Focuses on day, date, and month calculations.
Includes odd days, leap years, and recurring cycles.
Requires memorizing month codes or shortcuts.
Used in logic-based date prediction scenarios.
Practiced often in reasoning and aptitude sections.`,
 link:"/questions/calendar",
 image:"https://placehold.co/150?text=Calendar%20Problems",
 },
 {
 title:"Clock Problems",
 description: `Involves angles between clock hands and meeting times.
Tests visual and mathematical understanding of time.
Formulas help in quick solving of hour-minute gaps.
Also includes mirror and incorrect time concepts.
Regular practice improves speed and accuracy.`,
 link:"/questions/clocks",
 image:"https://placehold.co/150?text=Clock%20Problems",
 },
 {
 title:"Analytical Reasoning",
 description: `Involves multi-condition, logic-based problem solving.
Includes grouping, assignments, and layered constraints.
Often used in high-level aptitude and management tests.
Requires interpreting complex instructions logically.
Tabular solving and elimination are key strategies.`,
 link:"/questions/analytical-reasoning",
 image:"https://placehold.co/150?text=Analytical%20Reasoning",
 },
 ],
};

const MockInterviewGrid = ({ mode = "learner" }) => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [showModal, setShowModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState("");

  const isTenantMode = mode === "tenant" || (!!tenant && window.location.hostname !== 'theskillgenie.com' && window.location.hostname !== 'www.theskillgenie.com' && window.location.hostname !== 'localhost');
  // NextGen Builder / TSG Nexus embeds this grid under
  // /nextgen-builder/mock-interview — back/practice navigation must stay
  // inside /nextgen-builder/*, never the college/tenant /student/* routes.
  const isNextGenBuilderRoute = window.location.pathname.startsWith('/nextgen-builder');
  const backPath = isNextGenBuilderRoute
    ? "/nextgen-builder/mock-interview"
    : (isTenantMode ? "/student/mockinterviewhome" : "/mockinterviewhome");
  const [userId, setUserId] = useState(() => {
    try {
      const userRaw = localStorage.getItem("user");
      const u = userRaw ? JSON.parse(userRaw) : null;
      return (u?.userId || "guest");
    } catch { return "guest"; }
  });
  const [plan, setPlan] = useState("free");

  useEffect(() => {
    async function fetchPlan() {
      if (tenant) {
        setPlan("premium");
        return;
      }
      try {
        const userRaw = localStorage.getItem("user");
        if (!userRaw) return;
        const parsedUser = JSON.parse(userRaw);
        const uid = parsedUser?.userId;
        if (!uid) return;

        const res = await fetch(`${API_BASE_URL}/check-payment/${uid}`, {
          headers: { "Content-Type": "application/json" },
          signal: AbortSignal.timeout(7000),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.paid) setPlan("premium");
        }
      } catch (err) {
        console.error("Error fetching payment info:", err);
      }
    }
    fetchPlan();
  }, [tenant]);

  const handleTopicClick = (title) => {
    // Determine the category of the topic for tracking
    // For simplicity, we track 'arithmetic' or 'time_management' as broad types
    // or we can track individual topics. The requirement says "5 interview modes"
    // so we probably should map these to 'arithmetic' or 'time_management'.
    
    let trackerType = "arithmetic"; 
    // Logic to find which section this title belongs to
    for (const [section, topics] of Object.entries(categorizedSections)) {
      if (topics.find(t => t.title === title)) {
        if (section === "Quantitative") trackerType = "arithmetic";
        else if (section === "Reasoning") trackerType = "time_management";
        else trackerType = "general_practice";
        break;
      }
    }

    if (isTenantMode || plan === "premium" || hasUnlimitedClicks(userId)) {
      const prefix = isNextGenBuilderRoute ? "/nextgen-builder/mock-interview" : (isTenantMode ? "/student" : "/mockinterview");
      navigate(`${prefix}/mock-test/${encodeURIComponent(title)}`);
      return;
    }

    recordButtonClick(userId, trackerType);
    const prefix = isNextGenBuilderRoute ? "/nextgen-builder/mock-interview" : (isTenantMode ? "/student" : "/mockinterview");
    navigate(`${prefix}/mock-test/${encodeURIComponent(title)}`);
  };

  return (
    <div className="min-h-screen bg-bgMain text-body p-8 md:p-12 relative overflow-hidden font-sans">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>

      <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-12 text-center uppercase tracking-tighter relative z-10">
        Interview Practice Modules
      </h1>

      {Object.entries(categorizedSections).map(([section, topics]) => (
        <div key={section} className="mb-20 relative z-10">
          <h2 className="text-2xl font-bold text-primary mb-8 uppercase tracking-widest border-l-4 border-primary pl-4">
            {section}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {topics.map((topic, index) => (
              <div
                key={index}
                onClick={() => handleTopicClick(topic.title)}
                className="bg-bgSection border-2 border-accentGray/30 rounded-[32px] shadow-xl p-8 flex flex-col items-start transition-all duration-300 hover:scale-[1.03] hover:border-primary hover:shadow-[0_0_30px_rgba(79, 70, 229,0.15)] cursor-pointer group relative overflow-hidden"
              >
                {/* Background glow */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-all"></div>

                <h3 className="text-2xl font-black mb-4 text-heading group-hover:text-primary transition-colors leading-tight tracking-tighter">
                  {topic.title}
                </h3>
                <p className="text-sm text-muted mb-8 leading-relaxed line-clamp-4 flex-grow">
                  {topic.description}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTopicClick(topic.title);
                  }}
                  className="w-full btn-primary-gradient py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg transition-all active:scale-95"
                >
                  Start Practice
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
      
      {/* Modal for upgrade prompt */}
      {showModal && (
        <div className="fixed inset-0 bg-bgMain/90 backdrop-blur-md flex items-center justify-center z-[100] p-6">
          <div className="bg-bgSection border-2 border-secondary/30 rounded-[48px] shadow-[0_0_60px_rgba(124,58,237,0.3)] p-10 md:p-12 max-w-md w-full text-center relative overflow-hidden animate-slideIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/5 rounded-full blur-3xl"></div>
            
            <div className="text-6xl mb-6">🔒</div>
            <h3 className="text-3xl text-white font-black mb-4 uppercase tracking-tighter leading-tight">
              Practice Limit Reached
            </h3>
            <p className="text-body/80 mb-8 text-sm font-medium leading-relaxed">
              You've used your <span className="text-primary font-black">1 free practice session</span> for this module.
              <br/><br/>
              Unlock <span className="text-primary font-black uppercase">SkillGenie Premium</span> for unlimited high-stakes interview practice!
            </p>
            
            <div className="bg-bgSoft border border-secondary/20 rounded-3xl p-6 mb-10 text-left">
              <ul className="space-y-3">
                {["Unlimited Practice Attempts", "Personalized AI Feedback", "Interview Results History", "Advanced Industry Analytics"].map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-[11px] font-black text-body uppercase tracking-widest">
                    <span className="text-primary">✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="flex-1 px-8 py-4 bg-bgMain text-muted border border-accentGray/30 rounded-2xl hover:text-white transition-all font-black uppercase text-xs tracking-widest"
                onClick={() => setShowModal(false)}
              >
                Later
              </button>
              <button 
                className="flex-[2] btn-primary-gradient px-8 py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-105 active:scale-95 transition-all uppercase tracking-tighter"
                onClick={() => {
                  setShowModal(false);
                  navigate("/skillgenpayments");
                }}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockInterviewGrid;




